import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { JWPlayer } from '../../../types/jwplayer';

const INJECT_DELAY_MS = 300;
const HIDE_DELAY_MS = 800;
const SLIDER_VISIBLE_CLASS = 'custom-volume-slider-visible';

export function useCustomVolumeSlider(playerRef: RefObject<JWPlayer | undefined>) {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const volumeHandlerRef = useRef<((ev: { volume?: number }) => void) | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const injectVolumeSlider = () => {
    const player = playerRef.current;
    const ctrlContainer = player?.getContainer?.();
    if (!ctrlContainer) return;

    const volumeIcon = ctrlContainer.querySelector('.jw-icon-volume');
    if (!volumeIcon?.parentNode) return;

    if (sliderRef.current) {
      sliderRef.current.remove();
      sliderRef.current = null;
      if (volumeHandlerRef.current) {
        player?.off?.('volume' as never, volumeHandlerRef.current);
      }
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'custom-volume-slider';
    wrapper.setAttribute('aria-label', 'Volume');

    const input = document.createElement('input');
    input.type = 'range';
    input.min = '0';
    input.max = '100';
    const initialVolume = Math.round(player?.getVolume?.() ?? 100);
    input.value = String(initialVolume);
    input.setAttribute('aria-label', 'Volume');

    const syncFillStyle = () => {
      wrapper.style.setProperty('--value', `${input.value}%`);
    };
    syncFillStyle();

    const onVolumeChange = (ev: { volume?: number }) => {
      const v = ev?.volume ?? player?.getVolume?.() ?? 100;
      input.value = String(Math.round(v));
      syncFillStyle();
    };
    volumeHandlerRef.current = onVolumeChange;
    player?.on?.('volume' as never, onVolumeChange);

    input.addEventListener('input', () => {
      const v = Number(input.value);
      player?.setVolume?.(v);
      syncFillStyle();
    });

    const showSlider = () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      wrapper.classList.add(SLIDER_VISIBLE_CLASS);
    };

    const scheduleHideSlider = () => {
      hideTimeoutRef.current = setTimeout(() => {
        wrapper.classList.remove(SLIDER_VISIBLE_CLASS);
        hideTimeoutRef.current = null;
      }, HIDE_DELAY_MS);
    };

    volumeIcon.addEventListener('mouseenter', showSlider);
    volumeIcon.addEventListener('mouseleave', scheduleHideSlider);
    wrapper.addEventListener('mouseenter', showSlider);
    wrapper.addEventListener('mouseleave', scheduleHideSlider);
    input.addEventListener('mouseenter', showSlider);
    input.addEventListener('mouseleave', scheduleHideSlider);

    wrapper.appendChild(input);
    volumeIcon.parentNode.insertBefore(wrapper, volumeIcon.nextSibling);
    sliderRef.current = wrapper as HTMLDivElement;
  };

  useEffect(() => {
    return () => {
      const player = playerRef.current;
      if (volumeHandlerRef.current && player) {
        player.off?.('volume' as never, volumeHandlerRef.current);
        volumeHandlerRef.current = null;
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      if (sliderRef.current) {
        sliderRef.current.remove();
        sliderRef.current = null;
      }
    };
  }, [playerRef]);

  return {
    injectVolumeSlider: () => {
      setTimeout(injectVolumeSlider, INJECT_DELAY_MS);
    },
  };
}
